/**
 * Scene scopes: per-banner, per-dimension override modes.
 *
 * A scene has one shared state — `titles.main`, `bgOpacity`, `layoutOption`
 * and the like — and a per-template key for every field that a banner may
 * diverge on. Which banner diverges on WHAT used to be one bit, the chain
 * (`locked`), covering texts and uploads together. Here it is a table:
 *
 *   templateModes[templateId][dimension] = "own" | "preset"   (absent: inherit)
 *
 * - inherit — the banner follows the shared value for that dimension.
 * - own     — the user set this banner apart on that dimension (from a
 *             selection scope, from the banner's own tool panel, or by
 *             editing its text). Shared changes leave it alone.
 * - preset  — the value came from the default scene. It shows with the chain
 *             intact, and the first shared change on that dimension drops it
 *             back to inherit without asking (`yieldPresets`).
 *
 * Resolution everywhere is `own → preset → shared`; the mode table decides
 * whether the banner's per-template key counts.
 *
 * Rolled out per channel: everything reading `isScopesChannel` keeps the old
 * chain behaviour where it returns false.
 */
import {TEXT_RECORD_FIELDS} from "contexts/edits";

export type TemplateMode = "own" | "preset";
export type TemplateModes = Record<string, Partial<Record<DimensionId, TemplateMode>>>;

export type DimensionId = "text" | "image" | "look" | "layout" | "variant";

export interface Dimension {
  id: DimensionId;
  label: string;
  /** One-letter glyph for the banner chip. */
  glyph: string;
  /** Per-template record fields in the edits context. */
  edits: readonly string[];
  /** Per-template record fields in the images context. */
  images: readonly string[];
  /** Per-template record fields in the setting context. */
  setting: readonly string[];
}

/**
 * The text records keyed by template id. `gdnTags` is a text record too, but
 * its keys are tag ids (`tag-0`, `tag-1`, …), not templates: read as
 * per-template keys they became "preset" banners, and the next shared text
 * edit cleaned them out of the record while `gdnTagOrder` kept naming them —
 * a default scene saved in that state failed to load for everyone.
 */
const TEMPLATE_TEXT_FIELDS: readonly string[] = TEXT_RECORD_FIELDS.filter((f) => f !== "gdnTags");

export const DIMENSIONS: readonly Dimension[] = Object.freeze([
  {id: "text", label: "Text", glyph: "T", edits: TEMPLATE_TEXT_FIELDS, images: [], setting: []},
  {
    id: "image",
    label: "Image",
    glyph: "▣",
    edits: [],
    images: ["customBgs", "customBgUploaded", "uploadImages"],
    setting: [],
  },
  {
    id: "look",
    label: "Look",
    glyph: "◐",
    edits: [],
    images: ["bgX", "bgY", "bgScale", "bgRotate", "bgFlip", "bgOpacities", "bgBrightnesses"],
    // The base colour is the fill the artwork sits on, so it belongs to the
    // look: "reset Look" puts it back with the fade and the crop.
    setting: ["bases"],
  },
  {id: "layout", label: "Layout", glyph: "◧", edits: [], images: [], setting: ["layouts"]},
  {id: "variant", label: "Variant", glyph: "V", edits: [], images: [], setting: ["variants"]},
]);

export const DIMENSION_BY_ID: Record<DimensionId, Dimension> = Object.fromEntries(
  DIMENSIONS.map((d) => [d.id, d])
) as Record<DimensionId, Dimension>;

/** Channels the scope model is live on. Others keep the chain as it was. */
export const SCOPE_CHANNELS: ReadonlySet<string> = new Set(["releases"]);

export function isScopesChannel(channelValue: string | null | undefined): boolean {
  return Boolean(channelValue) && SCOPE_CHANNELS.has(channelValue as string);
}

export function getTemplateMode(
  modes: TemplateModes | null | undefined,
  templateId: string,
  dimension: DimensionId
): TemplateMode | undefined {
  return modes?.[templateId]?.[dimension];
}

/** A new table with one cell changed; `undefined` clears the cell. */
export function withTemplateMode(
  modes: TemplateModes | null | undefined,
  templateId: string,
  dimension: DimensionId,
  mode: TemplateMode | undefined
): TemplateModes {
  const current = {...(modes?.[templateId] ?? {})};
  if (mode === undefined) delete current[dimension];
  else current[dimension] = mode;
  const next: TemplateModes = {...(modes ?? {})};
  if (Object.keys(current).length === 0) delete next[templateId];
  else next[templateId] = current;
  return next;
}

/**
 * A new table with every (id, dimension) cell set to `mode` in one pass. One
 * table, one write: two `withTemplateMode` calls that each start from the
 * table a component closed over would lose the first change to the second.
 */
export function withTemplateModes(
  modes: TemplateModes | null | undefined,
  templateIds: readonly string[],
  dimensions: readonly DimensionId[],
  mode: TemplateMode | undefined
): TemplateModes {
  let next: TemplateModes = modes ?? {};
  for (const id of templateIds) {
    for (const dimension of dimensions) next = withTemplateMode(next, id, dimension, mode);
  }
  return next;
}

/**
 * The value a look slider shows when no banner is selected.
 *
 * A banner follows the slider when it has no value of its own in `record`, or
 * a `preset` value: the first shared edit drops a preset (`yieldPresets`). If
 * all the banners that follow the slider show one value, the slider shows that
 * value. A slider that showed only the shared value showed 100 % over a banner
 * at 54 % (IJ What's New, 2026-10-27). Otherwise the slider shows the shared
 * value, and a drag makes the banners agree.
 */
export function sharedSliderValue(
  templateIds: readonly string[],
  record: Record<string, number | undefined> | null | undefined,
  shared: number,
  modes: TemplateModes | null | undefined
): number {
  const shown = new Set<number>();
  for (const id of templateIds) {
    const own = record?.[id];
    if (own === undefined) shown.add(shared);
    else if (getTemplateMode(modes, id, "look") === "preset") shown.add(own);
  }
  return shown.size === 1 ? [...shown][0] : shared;
}

/**
 * The table without a mode on `dimension` for any banner. The ALL view of
 * Releases keeps the texts of each product in the product's own keys
 * (AllProductsWhatsNew), so a text mode of one of its banners has no meaning
 * there, the same as the chain.
 */
export function withoutDimensionModes(
  modes: TemplateModes | null | undefined,
  dimension: DimensionId
): TemplateModes {
  let next: TemplateModes = modes ?? {};
  for (const [id, cell] of Object.entries(modes ?? {})) {
    if (cell?.[dimension] !== undefined) next = withTemplateMode(next, id, dimension, undefined);
  }
  return next;
}

/** Template ids holding `mode` on `dimension`. */
export function templatesInMode(
  modes: TemplateModes | null | undefined,
  dimension: DimensionId,
  mode: TemplateMode
): string[] {
  return Object.entries(modes ?? {})
    .filter(([, cell]) => cell?.[dimension] === mode)
    .map(([id]) => id);
}

/**
 * The per-template keys a scene carries for one dimension, as `{field: id[]}`
 * — `main` is the shared slot, not a template.
 */
export function perTemplateKeys(
  scene: Record<string, unknown>,
  dimension: Dimension
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const field of [...dimension.edits, ...dimension.images, ...dimension.setting]) {
    const record = scene[field];
    if (!record || typeof record !== "object") continue;
    const ids = Object.keys(record as Record<string, unknown>).filter(
      (id) => id !== "main" && (record as Record<string, unknown>)[id] !== undefined
    );
    if (ids.length) out[field] = ids;
  }
  return out;
}

/**
 * The modes a default scene implies: every banner with a per-template value
 * on a dimension is `preset` there. Defaults never carry `own` — the chain an
 * admin detached to author the value is not something users inherit.
 *
 * Look is the exception: every banner carries a crop (`bgX`…), so a crop alone
 * is not a preset. Only a per-template opacity or brightness makes one.
 */
export function presetModesFromScene(scene: Record<string, unknown>): TemplateModes {
  let modes: TemplateModes = {};
  for (const dimension of DIMENSIONS) {
    const keys = perTemplateKeys(scene, dimension);
    const fields =
      dimension.id === "look"
        ? Object.keys(keys).filter(
            (f) => f === "bgOpacities" || f === "bgBrightnesses" || f === "bases"
          )
        : Object.keys(keys);
    for (const field of fields) {
      for (const id of keys[field]) modes = withTemplateMode(modes, id, dimension.id, "preset");
    }
  }
  return modes;
}

/** Dimensions whose banners draw their per-template value in any mode. */
const MODELESS_RENDER_DIMENSIONS: readonly DimensionId[] = ["look", "layout", "variant"];

/**
 * The saved mode table, plus `preset` for each banner that has a
 * per-template value on look, layout, or variant and no mode there.
 *
 * On these dimensions the banner draws its value whatever its mode, but a
 * shared edit only clears the value on `preset` and `own` banners. Without a
 * mode the value cannot be changed from the sidebar. Saved scenes with this
 * state exist, so the load repairs it. Text and image read the mode when they
 * render, so they are left as saved.
 */
export function withUnmarkedPresets(
  modes: TemplateModes,
  scene: Record<string, unknown>
): TemplateModes {
  let next = modes;
  for (const [id, cell] of Object.entries(presetModesFromScene(scene))) {
    for (const dimension of MODELESS_RENDER_DIMENSIONS) {
      if (cell[dimension] && !getTemplateMode(next, id, dimension)) {
        next = withTemplateMode(next, id, dimension, "preset");
      }
    }
  }
  return next;
}

/** Per-template values of a scene, grouped `{templateId: {dimension: {field: value}}}`. */
export type TemplateValues = Record<string, Partial<Record<DimensionId, Record<string, unknown>>>>;

/**
 * What a default scene gave each banner, per dimension — the values "reset
 * to default" puts back. Crops are left out for the same reason they are not
 * presets: every banner has one.
 */
export function defaultTemplateValuesFromScene(scene: Record<string, unknown>): TemplateValues {
  const out: TemplateValues = {};
  for (const dimension of DIMENSIONS) {
    const keys = perTemplateKeys(scene, dimension);
    for (const [field, ids] of Object.entries(keys)) {
      if (
        dimension.id === "look" &&
        field !== "bgOpacities" &&
        field !== "bgBrightnesses" &&
        field !== "bases"
      )
        continue;
      for (const id of ids) {
        const value = (scene[field] as Record<string, unknown>)[id];
        out[id] = out[id] ?? {};
        out[id][dimension.id] = {...(out[id][dimension.id] ?? {}), [field]: value};
      }
    }
  }
  return out;
}

/** The per-banner scope records a default scene gives the editor on load. */
export interface DefaultScopeRecords {
  layouts: Record<string, unknown>;
  variants: Record<string, unknown>;
  bases: Record<string, unknown>;
  templateModes: TemplateModes;
  defaultTemplateValues: TemplateValues;
}

/**
 * Every scope record a default scene loads, in one place. The records are
 * `{}` when absent, so a previous scene's values cannot stay. The save writes
 * each of `layouts`, `variants`, and `bases`; a load that sets only some of
 * them marks the others as presets and draws the previous values.
 */
export function scopeRecordsFromDefault(scene: Record<string, unknown>): DefaultScopeRecords {
  const record = (field: string) =>
    scene[field] && typeof scene[field] === "object" ? (scene[field] as Record<string, unknown>) : {};
  return {
    layouts: record("layouts"),
    variants: record("variants"),
    bases: record("bases"),
    templateModes: presetModesFromScene(scene),
    defaultTemplateValues: defaultTemplateValuesFromScene(scene),
  };
}
